import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Card, Stack, TextField, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
    getDuplicateGroupLinksColumns,
    getDuplicateGroupSummaryColumns,
    getPocSummaryColumns,
} from "../../../utils/duplicateBankDetailsColumns";
import paymentportalservice from "../../../services/paymentportalservice";
import helpers from "../../../hooks/HelperFunctions";

// Same white-card convention as components/accessManagement/AccessManagementPage.jsx's
// table wrapper, so this section reads consistently against the gradient page canvas.
const CARD_SX = {
    p: { xs: 1.75, sm: 2.5 },
    borderRadius: 3,
    border: "1px solid rgba(0,0,0,0.06)",
    bgcolor: "#fff",
    boxShadow: 0,
};

function groupKey(group) {
    return `${group.ifscCode}|${group.accountNo}`;
}

export default function DuplicateBankDetailsReportTab() {
    const [pocSummary, setPocSummary] = useState([]);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryError, setSummaryError] = useState(null);
    const [pocSearch, setPocSearch] = useState("");

    const [selectedPocId, setSelectedPocId] = useState(null);
    const [pocGroups, setPocGroups] = useState([]); // this POC's duplicate bank-account groups
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [groupsError, setGroupsError] = useState(null);

    const [selectedGroupKey, setSelectedGroupKey] = useState(null);
    // Guards against an out-of-order response: clicking POC A then quickly
    // POC B must not let A's slower response land last and overwrite B's
    // rows under B's still-showing heading. Same pattern PaymentStatus.jsx
    // uses for its own request race.
    const latestGroupsRequestId = useRef(0);

    const pocColumns = useMemo(() => getPocSummaryColumns(), []);
    const groupColumns = useMemo(() => getDuplicateGroupSummaryColumns(), []);
    const linkColumns = useMemo(() => getDuplicateGroupLinksColumns(), []);

    useEffect(() => {
        setSummaryLoading(true);
        setSummaryError(null);
        paymentportalservice
            .FetchDuplicateBankDetailsSummary()
            .then((data) => setPocSummary(data?.pocSummary || []))
            .catch((error) => {
                console.error("Error fetching duplicate bank details summary:", error);
                setSummaryError(helpers.describeApiError(error));
            })
            .finally(() => setSummaryLoading(false));
    }, []);

    // The whole POC summary is already loaded client-side (one row per POC, a
    // few dozen at most) — filtering here avoids a round trip for something
    // this small.
    const filteredPocSummary = useMemo(() => {
        const query = pocSearch.trim().toLowerCase();
        if (!query) return pocSummary;
        return pocSummary.filter((row) => row.pocName?.toLowerCase().includes(query));
    }, [pocSummary, pocSearch]);

    const selectedPocName = useMemo(
        () => pocSummary.find((p) => p.pocId === selectedPocId)?.pocName,
        [pocSummary, selectedPocId]
    );

    function loadPocGroups(pocId) {
        const requestId = ++latestGroupsRequestId.current;
        setSelectedPocId(pocId);
        setSelectedGroupKey(null);
        setGroupsLoading(true);
        setGroupsError(null);
        paymentportalservice
            .FetchDuplicateBankDetailsForPoc(pocId)
            .then((data) => {
                if (latestGroupsRequestId.current !== requestId) return; // superseded by a later click
                // Campaign count and amount paid aren't precomputed server-side —
                // derived here from each group's own links, so no backend change
                // was needed to add them.
                const groups = (data?.duplicateGroups || []).map((group) => ({
                    ...group,
                    distinctCampaignCount: new Set(group.links.map((l) => l.campaignId)).size,
                    // payableAmount (net, after GST/vendor %) — not cost (gross Total
                    // Amount) — this report is about money that actually moved.
                    totalPayableAmount: group.links.reduce((sum, l) => sum + (l.payableAmount || 0), 0),
                }));
                setPocGroups(groups);
            })
            .catch((error) => {
                if (latestGroupsRequestId.current !== requestId) return;
                console.error("Error fetching duplicate bank details for POC:", error);
                setGroupsError(helpers.describeApiError(error));
            })
            .finally(() => {
                if (latestGroupsRequestId.current === requestId) setGroupsLoading(false);
            });
    }

    const selectedGroup = useMemo(
        () => pocGroups.find((g) => groupKey(g) === selectedGroupKey),
        [pocGroups, selectedGroupKey]
    );

    return (
        <Stack rowGap={2.5}>
            {summaryError && <Alert severity="error">{summaryError}</Alert>}

            <Card sx={CARD_SX}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    columnGap={2}
                    rowGap={1}
                    sx={{ mb: 1.5 }}
                >
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Bank accounts used per ops executive</Typography>
                    <TextField
                        size="small"
                        sx={{ minWidth: 240 }}
                        label="Search POC"
                        value={pocSearch}
                        onChange={(e) => setPocSearch(e.target.value)}
                    />
                </Stack>
                <Box sx={{ width: "100%" }}>
                    <DataGrid
                        autoHeight
                        loading={summaryLoading}
                        columns={pocColumns}
                        rows={filteredPocSummary}
                        getRowId={(row) => row.pocId}
                        density="compact"
                        showCellVerticalBorder
                        showColumnVerticalBorder
                        onRowClick={({ row }) => {
                            if ((row?.duplicatedBankAccountCount || 0) > 0) loadPocGroups(row.pocId);
                        }}
                        getRowClassName={({ row }) =>
                            (row?.duplicatedBankAccountCount || 0) > 0 ? "reports-clickable-row" : ""
                        }
                        sx={{ border: "none", "& .reports-clickable-row": { cursor: "pointer" } }}
                        initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
                        pageSizeOptions={[25, 50, 100]}
                    />
                </Box>
            </Card>

            {selectedPocId && (
                <Card sx={CARD_SX}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.5 }}>
                        Bank accounts reused by {selectedPocName || "this POC"}
                    </Typography>

                    {groupsError && (
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                            {groupsError}
                        </Alert>
                    )}

                    <Box sx={{ width: "100%" }}>
                        <DataGrid
                            autoHeight
                            loading={groupsLoading}
                            columns={groupColumns}
                            rows={pocGroups}
                            getRowId={groupKey}
                            density="compact"
                            showCellVerticalBorder
                            showColumnVerticalBorder
                            onRowClick={({ row }) => setSelectedGroupKey(groupKey(row))}
                            getRowClassName={() => "reports-clickable-row"}
                            sx={{ border: "none", "& .reports-clickable-row": { cursor: "pointer" } }}
                            initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
                            pageSizeOptions={[25, 50, 100]}
                        />
                    </Box>
                </Card>
            )}

            {selectedGroup && (
                <Card sx={CARD_SX}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.5 }}>
                        {selectedPocName || "This POC"}'s payments on {selectedGroup.ifscCode} · account ending{" "}
                        {selectedGroup.accountNo?.slice(-4)}
                    </Typography>

                    <Box sx={{ width: "100%" }}>
                        <DataGrid
                            autoHeight
                            columns={linkColumns}
                            rows={selectedGroup.links}
                            getRowId={(row) => row.paymentLinkId}
                            density="compact"
                            showCellVerticalBorder
                            showColumnVerticalBorder
                            sx={{ border: "none" }}
                            initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
                            pageSizeOptions={[25, 50, 100]}
                        />
                    </Box>
                </Card>
            )}
        </Stack>
    );
}
